import { useState, useMemo } from 'react';
import {
  Clock,
  CheckCircle,
  Target,
  Brain,
  UserCheck,
  Loader2,
  AlertCircle,
  BookOpen,
  FileText,
  Video,
  XCircle,
} from 'lucide-react';
import { useGetPatientAssignments } from '../../services/query/education/education';
import { EducationMetrics } from '../../services/api/education/educationApi';
import Pagination from '../shared/Pagination';
import type { PaginationType } from '../../types';

const STATUS_STYLES: Record<string, string> = {
  sent: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
};

const STATUS_ICON: Record<string, JSX.Element> = {
  sent: <CheckCircle className="w-3.5 h-3.5 mr-1" />,
  pending: <Clock className="w-3.5 h-3.5 mr-1" />,
  failed: <XCircle className="w-3.5 h-3.5 mr-1" />,
};

const CONTENT_TYPE_ICON: Record<string, JSX.Element> = {
  video: <Video className="w-4 h-4" />,
  article: <FileText className="w-4 h-4" />,
  pdf: <BookOpen className="w-4 h-4" />,
  infographic: <BookOpen className="w-4 h-4" />,
};

function truncate(text: string | null | undefined, max = 50): string {
  if (!text) return '—';
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface Params{
  metricsData?: EducationMetrics;
  metricsLoading?: boolean;
}

export default function PatientSpecificTab({ metricsData, metricsLoading }: Params) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data, isLoading, isError } = useGetPatientAssignments({
    page: currentPage,
    limit: itemsPerPage,
  });

  const assignments = data?.data ?? [];

  // Transform API pagination response to PaginationType
  const paginationData: PaginationType = useMemo(() => ({
    page: data?.pagination?.page ?? currentPage,
    limit: data?.pagination?.limit ?? itemsPerPage,
    total_pages: data?.pagination?.total_pages ?? 0,
    total_items: data?.pagination?.total_items ?? 0,
    total_appointments: data?.pagination?.total_items ?? 0,
    appointments_per_page: data?.pagination?.limit ?? itemsPerPage,
    has_next_page: data?.pagination?.has_next_page ?? false,
    has_previous_page: data?.pagination?.has_previous_page ?? false,
  }), [data, currentPage, itemsPerPage]);

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-blue-900">Total</h3>
            <UserCheck className="w-6 h-6 text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-blue-900">{metricsLoading ? '—' : metricsData?.assignments?.total}</p>
          <p className="text-xs text-blue-700 mt-1">All records</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-green-900">Sent</h3>
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-green-900">{metricsLoading ? '—' : metricsData?.assignments?.sent}</p>
          <p className="text-xs text-green-700 mt-1">Successfully delivered</p>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-yellow-900">Pending</h3>
            <Target className="w-6 h-6 text-yellow-600" />
          </div>
          <p className="text-3xl font-bold text-yellow-900">{metricsLoading ? '—' : metricsData?.assignments?.pending}</p>
          <p className="text-xs text-yellow-700 mt-1">Awaiting delivery</p>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-red-900">Failed</h3>
            <Brain className="w-6 h-6 text-red-600" />
          </div>
          <p className="text-3xl font-bold text-red-900">{metricsLoading ? '—' : metricsData?.assignments?.failed}</p>
          <p className="text-xs text-red-700 mt-1">Delivery errors</p>
        </div>
      </div>

      {/* Assignments Table */}
      <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <Clock className="w-6 h-6 mr-2 text-green-600" />
          Patient Assignments
        </h2>

        {isLoading && (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <Loader2 className="w-8 h-8 animate-spin mr-2" />
            <span>Loading assignments...</span>
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center py-12 text-red-500">
            <AlertCircle className="w-8 h-8 mr-2" />
            <span>Failed to load assignments. Please try again.</span>
          </div>
        )}

        {!isLoading && !isError && assignments.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No assignments found</p>
          </div>
        )}

        {!isLoading && !isError && assignments.length > 0 && (
          <div className="w-full overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-[900px] w-full text-sm text-left">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3 min-w-[180px]">Content</th>
                  <th className="px-4 py-3 min-w-[160px]">Diagnosis</th>
                  <th className="px-4 py-3 min-w-[100px]">Type</th>
                  <th className="px-4 py-3 min-w-[110px]">Status</th>
                  <th className="px-4 py-3 min-w-[130px]">Scheduled</th>
                  <th className="px-4 py-3 min-w-[120px]">Sent At</th>
                  <th className="px-4 py-3 min-w-[120px]">Return Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {assignments.map((assignment) => (
                  <tr key={assignment.id} className="hover:bg-gray-50 transition-colors">

                    {/* Content */}
                    <td className="px-4 py-3">
                      <p
                        className="font-medium text-gray-900"
                        title={assignment.content_title}
                      >
                        {truncate(assignment.content_title)}
                      </p>
                      {assignment.content_description && (
                        <p
                          className="text-xs text-gray-500 mt-0.5"
                          title={assignment.content_description}
                        >
                          {truncate(assignment.content_description)}
                        </p>
                      )}
                    </td>

                    {/* Diagnosis */}
                    <td className="px-4 py-3">
                      <span
                        className="text-gray-700 block"
                        title={assignment.diagnosis}
                      >
                        {truncate(assignment.diagnosis)}
                      </span>
                      {assignment.treatment_plan && (
                        <p
                          className="text-xs text-gray-400 mt-0.5"
                          title={assignment.treatment_plan}
                        >
                          {truncate(assignment.treatment_plan)}
                        </p>
                      )}
                    </td>

                    {/* Content Type */}
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1 text-gray-600 capitalize">
                        {CONTENT_TYPE_ICON[assignment.content_type] ?? <BookOpen className="w-4 h-4" />}
                        {assignment.content_type}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[assignment.status] ?? 'bg-gray-100 text-gray-700'}`}>
                        {STATUS_ICON[assignment.status]}
                        {assignment.status}
                      </span>
                      {assignment.failure_reason && (
                        <p
                          className="text-xs text-red-500 mt-1"
                          title={assignment.failure_reason}
                        >
                          {truncate(assignment.failure_reason)}
                        </p>
                      )}
                    </td>

                    {/* Scheduled Date */}
                    <td className="px-4 py-3 text-gray-600">
                      <span>{formatDate(assignment.scheduled_send_date)}</span>
                      {assignment.scheduled_send_time && (
                        <p className="text-xs text-gray-400">{assignment.scheduled_send_time}</p>
                      )}
                    </td>

                    {/* Sent At */}
                    <td className="px-4 py-3 text-gray-500">
                      {formatDate(assignment.sent_at)}
                    </td>

                    {/* Return Date */}
                    <td className="px-4 py-3 text-gray-500">
                      {formatDate(assignment.return_date)}
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !isError && assignments.length > 0 && (
          <Pagination
            pagination={paginationData}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            itemName="assignments"
          />
        )}
      </div>
    </div>
  );
}
